import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-hot-toast';
import { AnimatePresence, motion } from 'framer-motion';
import PhotoslopLibrary from '../components/photoslop/PhotoslopLibrary';
import PhotoslopCompose from '../components/photoslop/PhotoslopCompose';
import PhotoslopOutput from '../components/photoslop/PhotoslopOutput';
import {
  assetUrl,
  createSpiceFolder,
  fetchJob,
  fetchLibrary,
  fetchSpices,
  postGenerate,
  uploadSpiceRef,
} from '../utils/photoslopApi';
import { initialPhotoslopState, photoslopReducer } from '../utils/photoslopState';
import { fetchTokenBalance } from '../utils/tokenUtils';
import { FOUNDER_EMAILS } from '../config';
import { photoslopBypassTokens } from '../config/photoslop';
import {
  apiCreateDeckFromImage,
  configureDeckApi,
} from '../lib/deck/api-client';
import { getAuthHeaders } from '../utils/auth';

const STYLING_HINTS = {
  'liquid-glass':
    'Clean composition with space in the lower portion for a glass text card overlay. Soft natural lighting.',
  'social-post':
    'Scene/mood background with clear space in the lower third for text overlay. Cinematic lighting.',
  'eggshell-bird':
    'Integrated into a solid-color matte background with matching ambient light. Eggshell texture.',
  'precomp-hero':
    'Bird positioned on the right side of frame, facing left. Clean cream background.',
  'event-graphic': 'Dramatic atmosphere, dark moody background, cinematic event-poster lighting.',
  'clean-bird':
    'Full body visible, clean cream background, centred composition, soft natural studio lighting.',
  tags: 'Robin positioned with clear space for text overlay on cream background.',
};

function pickDefaultSpice(folders) {
  if (!folders?.length) return null;
  const robin = folders.find((f) => f.key === 'robin-ref' && f.count > 0);
  if (robin) return robin.key;
  const withAssets = folders.find((f) => f.count > 0);
  return (withAssets || folders[0]).key;
}

export default function PhotoslopCreate({
  tokenBalance = 0,
  onTokenBalanceChange,
  monthlyAllowance = 0,
}) {
  const [state, dispatch] = useReducer(photoslopReducer, initialPhotoslopState);
  const navigate = useNavigate();
  const { getAccessTokenSilently, user, isAuthenticated, isLoading: authLoading } = useAuth0();
  const pollAbort = useRef(null);
  const [addingDeck, setAddingDeck] = useState(null);
  const [spiceOpen, setSpiceOpen] = useState(true);

  const [spices, setSpices] = React.useState(null);
  const [library, setLibrary] = React.useState(null);
  const [loadErr, setLoadErr] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const didSetDefaultSpice = useRef(false);

  const isFounder = user?.email && FOUNDER_EMAILS.includes(user.email);
  const bypassTokens = photoslopBypassTokens();
  const availableTokens =
    bypassTokens || tokenBalance === Infinity || tokenBalance > 9999
      ? Infinity
      : tokenBalance;

  const refCount = (state.root ? 1 : 0) + state.secondaries.length;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, lib] = await Promise.all([
        fetchSpices(),
        isAuthenticated
          ? fetchLibrary(getAccessTokenSilently)
          : Promise.resolve({ generated: [] }),
      ]);
      setSpices(s);
      setLibrary(lib);
      setLoadErr(null);
      const defaultKey = pickDefaultSpice(s.folders);
      if (defaultKey && !didSetDefaultSpice.current) {
        didSetDefaultSpice.current = true;
        dispatch({ type: 'SET_ACTIVE_FOLDER', folderKey: defaultKey });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setLoadErr(msg);
      toast.error(msg, { duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!loading && !state.root) {
      setSpiceOpen(true);
    }
  }, [loading, state.root]);

  const refetchLibrary = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const lib = await fetchLibrary(getAccessTokenSilently);
      setLibrary(lib);
    } catch {
      /* non-fatal */
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    if (isAuthenticated && onTokenBalanceChange) {
      fetchTokenBalance(getAccessTokenSilently)
        .then(onTokenBalanceChange)
        .catch(() => {});
    }
  }, [isAuthenticated, getAccessTokenSilently, onTokenBalanceChange]);

  const refreshBalance = useCallback(async () => {
    if (onTokenBalanceChange) {
      const bal = await fetchTokenBalance(getAccessTokenSilently);
      onTokenBalanceChange(bal);
    }
  }, [getAccessTokenSilently, onTokenBalanceChange]);

  const handleSend = useCallback(async () => {
    if (!state.root || !state.promptText.trim()) return;
    pollAbort.current?.abort();
    const ac = new AbortController();
    pollAbort.current = ac;

    const images = [state.root, ...state.secondaries].map((s) => assetUrl(s.path));
    let prompt = state.promptText.trim();
    if (state.styling && STYLING_HINTS[state.styling]) {
      prompt = `${prompt}. ${STYLING_HINTS[state.styling]}`;
    }

    dispatch({ type: 'SET_GENERATE_ERROR', msg: null });

    const jobIds = [];
    for (let i = 0; i < state.count; i++) {
      try {
        const res = await postGenerate(getAccessTokenSilently, {
          images,
          prompt,
          aspectRatio: state.aspect,
          brandSlug: 'robin',
        });
        jobIds.push(res.jobId);
      } catch (e) {
        jobIds.push(null);
        if (i === 0) {
          dispatch({
            type: 'SET_GENERATE_ERROR',
            msg: e instanceof Error ? e.message : String(e),
          });
          return;
        }
      }
    }

    dispatch({ type: 'START_GENERATION', jobIds });
    setSpiceOpen(false);
    await refreshBalance();

    const pending = new Set(jobIds.map((_, i) => i));
    try {
      while (pending.size > 0 && !ac.signal.aborted) {
        await new Promise((r) => setTimeout(r, 1500));
        await Promise.all(
          [...pending].map(async (i) => {
            const id = jobIds[i];
            if (!id) {
              dispatch({
                type: 'UPDATE_SLOT',
                index: i,
                patch: { status: 'failed', error: 'No job id' },
              });
              pending.delete(i);
              return;
            }
            try {
              const j = await fetchJob(getAccessTokenSilently, id);
              if (j.status === 'done' && j.imagePath) {
                dispatch({
                  type: 'UPDATE_SLOT',
                  index: i,
                  patch: { status: 'done', imagePath: j.imagePath, jobId: id },
                });
                pending.delete(i);
                void refetchLibrary();
              } else if (j.status === 'failed') {
                dispatch({
                  type: 'UPDATE_SLOT',
                  index: i,
                  patch: { status: 'failed', error: j.error || 'Failed', jobId: id },
                });
                pending.delete(i);
              }
            } catch {
              dispatch({
                type: 'UPDATE_SLOT',
                index: i,
                patch: { status: 'failed', error: 'Poll failed' },
              });
              pending.delete(i);
            }
          }),
        );
      }
    } finally {
      dispatch({ type: 'SET_GENERATING', v: false });
      await refreshBalance();
      void refetchLibrary();
      toast.success('Ready — tap Copy image');
    }
  }, [
    state.root,
    state.secondaries,
    state.promptText,
    state.styling,
    state.count,
    state.aspect,
    getAccessTokenSilently,
    refreshBalance,
    refetchLibrary,
  ]);

  useEffect(() => {
    configureDeckApi(() => getAuthHeaders(getAccessTokenSilently));
  }, [getAccessTokenSilently]);

  const onAddToDeck = useCallback(
    async (path) => {
      setAddingDeck(path);
      try {
        const deck = await apiCreateDeckFromImage(assetUrl(path), 'From Create');
        toast.success('Added to deck');
        navigate(`/decks/${deck.id}/edit`);
      } catch (e) {
        toast.error(e?.message || 'Could not create deck');
      } finally {
        setAddingDeck(null);
      }
    },
    [navigate],
  );

  const onUseAsRoot = useCallback((path) => {
    dispatch({
      type: 'SET_ROOT',
      root: { path, source: 'yours' },
    });
    dispatch({ type: 'SET_ACTIVE_FOLDER', folderKey: 'yours' });
    toast.success('Root set (@1)');
  }, []);

  const insertToken = useCallback((n) => {
    const token = `@${n}`;
    const cur = state.promptText;
    if (cur.includes(token)) return;
    dispatch({
      type: 'SET_PROMPT',
      text: cur.trim() ? `${cur.trimEnd()} ${token}` : token,
    });
  }, [state.promptText]);

  if (authLoading) {
    return (
      <div className="flex h-[calc(100dvh-6rem)] items-center justify-center bg-[#F8F7F4]">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#00FF9F] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="relative flex h-[calc(100dvh-6rem)] min-h-0 flex-col overflow-hidden bg-[#F8F7F4]">
      <header className="flex flex-shrink-0 items-center gap-2 border-b border-black/5 bg-[#F8F7F4] px-3 py-2 sm:px-4">
        <button
          type="button"
          onClick={() => setSpiceOpen(true)}
          className={`rounded-lg border px-3 py-1.5 font-['VT323'] text-[16px] transition ${
            spiceOpen
              ? 'border-[#00FF9F] bg-[#00FF9F]/15 text-black'
              : 'border-black/10 bg-white text-black/60 hover:border-[#00FF9F]/30'
          }`}
        >
          Refs{refCount > 0 ? ` (${refCount})` : ''}
        </button>
        <h1 className="min-w-0 flex-1 truncate font-['VT323'] text-[20px] text-black/70 sm:text-[22px]">
          Spice rack
        </h1>
      </header>

      <div className="flex min-h-0 flex-1 flex-col">
        <PhotoslopOutput
          slots={state.outputSlots}
          history={library?.generated?.slice(0, 40) ?? []}
          onUseAsRoot={onUseAsRoot}
          onAddToDeck={onAddToDeck}
          addingDeck={addingDeck}
          generating={state.generating}
        />

        <PhotoslopCompose
          root={state.root}
          secondaries={state.secondaries}
          promptText={state.promptText}
          styling={state.styling}
          count={state.count}
          aspect={state.aspect}
          generating={state.generating}
          generateError={state.generateError}
          availableTokens={availableTokens}
          isFounder={isFounder}
          monthlyUsage={monthlyAllowance}
          onPromptChange={(text) => dispatch({ type: 'SET_PROMPT', text })}
          onRebuildPrompt={() => dispatch({ type: 'REBUILD_PROMPT' })}
          onStylingChange={(styling) => dispatch({ type: 'SET_STYLING', styling })}
          onCount={(count) => dispatch({ type: 'SET_COUNT', count })}
          onAspect={(aspect) => dispatch({ type: 'SET_ASPECT', aspect })}
          onSend={handleSend}
          onRemoveSecondary={(path) => dispatch({ type: 'REMOVE_SECONDARY', path })}
          onClearRoot={() => dispatch({ type: 'CLEAR_ROOT' })}
          onInsertToken={insertToken}
          onOpenSpices={() => setSpiceOpen(true)}
        />
      </div>

      <AnimatePresence>
        {spiceOpen ? (
          <>
            <motion.button
              type="button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              aria-label="Close references"
              className="absolute inset-0 z-40 bg-black/40"
              onClick={() => setSpiceOpen(false)}
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              className="absolute bottom-0 left-0 top-0 z-50 flex w-[min(420px,92vw)] flex-col border-r border-black/10 bg-[#F8F7F4] shadow-2xl"
            >
              <PhotoslopLibrary
                spices={spices}
                library={library}
                activeFolderKey={state.activeFolderKey}
                root={state.root}
                secondaries={state.secondaries}
                pickingRoot={state.pickingRoot}
                loading={loading}
                loadError={loadErr}
                onActiveFolderChange={(key) =>
                  dispatch({ type: 'SET_ACTIVE_FOLDER', folderKey: key })
                }
                onPickRoot={(root) => {
                  dispatch({ type: 'SET_ROOT', root });
                }}
                onToggleSecondary={(asset) =>
                  dispatch({ type: 'TOGGLE_SECONDARY', asset })
                }
                onUpload={(spiceKey, file) =>
                  uploadSpiceRef(getAccessTokenSilently, spiceKey, file)
                }
                onRefreshSpices={loadData}
                onCreateSpice={(name) => createSpiceFolder(getAccessTokenSilently, name)}
                onClose={() => setSpiceOpen(false)}
              />
            </motion.aside>
          </>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
